import { Injectable } from '@angular/core'

/**
 * Tracks the in-flight router view transition so features that animate on
 * arrival can wait for the page to stop moving first.
 *
 * Angular resolves a navigation as the view transition *begins* — `NavigationEnd`
 * fires while the shell is still sliding for another few hundred milliseconds.
 * Anything that reacts to that event by animating (the guided tour spotlighting
 * its next anchor) ends up running against the page transition rather than
 * after it, which reads as two competing animations and leaves the highlight
 * trailing the element it belongs to.
 *
 * Fed by the `onViewTransitionCreated` hook in app.config.ts.
 */
@Injectable({ providedIn: 'root' })
export class ViewTransitionService {
  private settled: Promise<void> = Promise.resolve()

  /** Record a transition that just started. */
  track(transition: ViewTransition): void {
    // `finished` rejects when the transition is skipped or abandoned; either
    // way the page has stopped moving, which is all callers are waiting on.
    this.settled = transition.finished.catch(() => undefined)
  }

  /** Resolves once no router view transition is animating. */
  whenSettled(): Promise<void> {
    return this.settled
  }
}
